import { classNames, cn } from '@commenda/utils';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';

import * as React from 'react';

import * as RPNInput from 'react-phone-number-input';

import * as flags from 'country-flag-icons/react/1x1';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../internal/Command';
import { Input, InputProps } from '../Input';
import { InputErrorMessage } from '../InputErrorMessage';
import { InputText } from '../InputText';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { ScrollArea } from '../ScrollArea';

// Alias to satisfy JSX PascalCase lint rule
const PhoneNumberInput = RPNInput.default;

// Define FlagComponent before usage to satisfy lints
const FlagComponent = ({
  country,
  countryName,
  _className = '',
}: RPNInput.FlagProps & { _className?: string }) => {
  const Flag = flags[country];

  return (
    <span className={cn('size-4 shrink-0', _className)}>
      {Flag && <Flag title={countryName} className="rounded-full" />}
    </span>
  );
};
FlagComponent.displayName = 'FlagComponent';

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn(
        'rounded-s-none border-l-0 focus:outline-none focus:border-l-0',
        className
      )}
      fullWidth
      {...props}
      ref={ref}
    />
  )
);
InputComponent.displayName = 'InputComponent';

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
};

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Reset search when popover closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const handleSelect = React.useCallback(
    (country: RPNInput.Country) => {
      onChange(country);
      setOpen(false);
    },
    [onChange]
  );

  const handleSearchChange = React.useCallback((search: string) => {
    setSearchQuery(search);
  }, []);

  const handleScroll = React.useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  const filteredOptions = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    const filtered = options.filter((option) => {
      if (!option?.label) return false;
      const label = option.label.toLowerCase();
      return label.includes(query);
    });

    const priorityCountries = ['US', 'IN', 'GB', 'SG', 'AE', 'IE'];

    return filtered.sort((a, b) => {
      const indexA = priorityCountries.indexOf(a.value);
      const indexB = priorityCountries.indexOf(b.value);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return a.label.localeCompare(b.label);
    });
  }, [options, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={open}
          className={classNames(
            // Base sizing and layout to align with other triggers
            'flex items-center gap-md h-10 shrink-0 rounded-e-none rounded-s-md pl-3 py-2 bg-bg-primary text-text-primary txt-small-regular box-border ring-0 transition-colors pr-md',
            // Disabled state and baseline focus ring
            'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
            // Border defaults and hover/focus colors
            'border border-border-primary hover:border-border-primary-hover',
            // Open state ring (strong) and hide border to avoid double outline
            open ? 'ring-4 ring-[var(--color-bg-brand-hover)] border-fg-brand' : '',
            disabled ? 'disabled:border-border-disabled' : ''
          )}
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronsUpDown
            className={cn(
              'h-4 w-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 px-0 py-0" align="start">
        <Command shouldFilter={false}>
          <CommandList className="w-80">
            <CommandInput
              placeholder="Search by country name..."
              className="w-64"
              value={searchQuery}
              onValueChange={handleSearchChange}
            />
            <ScrollArea
              className="h-[230px]"
              onWheel={handleScroll}
              type="always"
            >
              {filteredOptions.length === 0 ? (
                <CommandEmpty>No country found.</CommandEmpty>
              ) : (
                <CommandGroup
                  className="flex flex-col gap-px"
                  onWheel={handleScroll}
                >
                  {filteredOptions
                    .filter((x) => x.value)
                    .map((option) => (
                      <CommandItem
                        className={classNames(
                          'gap-md hover:bg-bg-active-light hover:cursor-pointer pl-2 py-2.5 pr-2.5 [&:not(:last-of-type)]:mb-px flex items-center',
                          option.value === value ? 'bg-bg-active-light' : ''
                        )}
                        key={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <FlagComponent
                          country={option.value}
                          countryName={option.label}
                        />
                        {option.value && (
                          <span className="txt-small-medium text-text-quaternary w-9">
                            {`+${RPNInput.getCountryCallingCode(option.value)}`}
                          </span>
                        )}
                        <span className="flex-1 txt-small-regular text-text-secondary">
                          {option.label}
                        </span>
                        {option.value === value ? (
                          <CheckIcon className="h-4 w-4 shrink-0" />
                        ) : null}
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value | undefined) => void;
    error?: string;
    hint?: string;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ className, onChange, error, value, hint, ...props }, ref) => {
      const handleChange = React.useCallback(
        (newValue: RPNInput.Value | undefined) => {
          if (onChange) onChange(newValue);
        },
        [onChange]
      );

      return (
        <div className="flex flex-col w-full gap-2">
          <PhoneNumberInput
            ref={ref}
            className={cn(
              'flex items-center rounded-lg h-9',
              error
                ? 'border border-border-error focus-visible:border-border-error'
                : '',
              className
            )}
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={InputComponent}
            /**
             * Handles the onChange event.
             *
             * react-phone-number-input might trigger the onChange event as undefined
             * when a valid phone number is not entered. To prevent this,
             * the value is coerced to an empty string.
             *
             * @param {E164Number | undefined} value - The entered value
             */
            onChange={handleChange}
            value={value}
            international
            {...props}
          />
          {error ? (
            <InputErrorMessage message={error} />
          ) : hint ? (
            <InputText accent="hint">{hint}</InputText>
          ) : null}
        </div>
      );
    }
  );
PhoneInput.displayName = 'PhoneInput';

export { FlagComponent, PhoneInput };
export type CountryCode = RPNInput.Country;
